import {action, computed, React, tinycolor, useLocalStore, observer, Popover2} from '@api'
import {Popover} from '@blueprintjs/core'
import {CatalogItemSidebarDetailsStore, useCatalogItemSidebarDetailsStore} from '@modules'
import {TextInput} from 'centric-ui-components'
import {observable} from 'mobx'
import {useContext} from 'react'
import {Hue, Saturation} from 'react-color/lib/components/common'
import * as css from './ColorPicker.css'
import {SketchPicker} from 'react-color'
import { CatalogItemDetailsStore } from '@modules/drawer'

interface IRGB {
  red: number
  green: number
  blue: number
}

interface IHSB {
  hue:  number
  saturation: number
  brightness: number
}
interface IColorPicker {
  color: string & IRGB & IHSB
  hex?: string
  colorField?: string | any
  detailStore: any
}

type colorType = 'hsv' | 'hsl' | 'rgb' | 'hex' | 'r' | 'g' | 'b'

class ColorPickerStore {
  @observable hex: string = null
  constructor(private store: CatalogItemDetailsStore) {
    this.hex = store?.data?.hex
  }

  @computed get color() {
    const color = tinycolor(this.hex)
    return {
      hex: `#${color.toHex()}`,
      hsl: color.toHsl(),
      hsv: color.toHsv(),
      rgb: color.toRgb()
    }
  }

  @action onChange = (value, type: colorType) => {
    if (type !== 'hex') {
      delete value.source
    }
    const c = _.clone(this.color)
    let val = {...value}
    switch (type) {
      case 'hex':
        val = value
        break
      case 'r':
      case 'g':
      case 'b':
        val = {
          ...c.rgb,
          [type]: value
        }
        break
      default:

    }
    const color = tinycolor(val),
      // hsb = color.toHsl(),
      hsb = color.toHsv(),
      rgb = color.toRgb(),
      update = {
        hex: `#${color.toHex()}`,
        hsb: {
          brightness: color.getBrightness(),
          hue: hsb.h,
          saturation: hsb.s * 100
        },
        rgb: {
          red: rgb.r,
          green: rgb.g,
          blue: rgb.b
        }
      }
    if (color.isValid()) {
      // this.store._changeColor(update)
      // this.store.onChange(update, "Color")
      this.hex = update.hex
    }
    return value
  }

  @computed get activeColor(){
    const color = tinycolor(this.hex)
    return {
      hex: `#${color.toHex()}`,
      hsl: color.toHsl(),
      hsv: color.toHsv(),
      rgb: color.toRgb()
    }
  }

  @action onColorChange = (color, fieldToUpdate) => {
    const { store: { data, updateFieldData, catalog: { isActionButtonEnabled, getColorValue, fieldByName } } } = this
    if (!isActionButtonEnabled('changeColor', data)) return null
    const catalogField = fieldByName(fieldToUpdate.field)
    if (catalogField?.editable) {
      updateFieldData(catalogField.field, getColorValue(catalogField, color))
    }

    //TODO create image using hex/color code then upload on image container of respective entity, based on config
    // const colorImage = (data?.images ?? []).find(img => img.rgba)
    // if (!data.images) {
    //   data.images = []
    // }

    // if (colorImage) {
    //   colorImage.rgba = color.rgb
    // } else {
    //   data.images.unshift({ rgba: color.rgb })
    // }
  }
}

export const ColorPickerStoreContext = React.createContext<ColorPickerStore>(undefined)
export const useColorPickerStore = () => useContext(ColorPickerStoreContext)

const MyPointer: React.FC<{}> = () => (<div className={css.pointer}/>)
const MySelector: React.FC<{}> = () => (<div className={css.selector}/>)

const MyColorPicker = observer(() => {
  const {color, onChange} = useColorPickerStore()
  return <div className={css.picker}>
    <div className={css.saturation}>
      <Saturation
        {...color}
        pointer={MySelector}
        onChange={(e, n) => { onChange(e, 'hsv') }}
      />
    </div>
    <div className={css.hueContainer}>
      <div style={{backgroundColor: color.hex}} className={css.swatches}/>
      <div className={css.hue}>
        <Hue
          {...color}
          onChange={(e, n) => { onChange(e, 'hsl') }}
          pointer={MyPointer}
        />
      </div>
    </div>
    <div className={css.hexRGBContainer}>
      <div className={css.rgbContainer}>
        <TextInput
          className={css.textField}
          name={'red'}
          label="R"
          value={color.rgb.r}
          onChange={(e) => { onChange(e.target.value, 'r') }}
        />
        <TextInput
          className={css.textField}
          name={'green'}
          label="G"
          value={color.rgb.g}
          onChange={(e) => { onChange(e.target.value, 'g') }}
        />
        <TextInput
          className={css.textField}
          name={'blue'}
          label="B"
          value={color.rgb.b}
          onChange={(e) => { onChange(e.target.value, 'b') }}
        />
      </div>
      <TextInput
        name={'hex'}
        label={'Hex'}
        className={css.hexField}
        value={color.hex}
        onChange={(e) => { onChange(e.target.value, 'hex') }}
        onBlur={(event) => {onChange(event.target.value, 'hex')}}
      />
    </div>
  </div>
})

export const ColorPicker = observer<IColorPicker>(({color, hex, colorField, detailStore}) => {
  const  store = useLocalStore(() => new ColorPickerStore(detailStore)),
    colorText = (_.isNumber((color as IRGB)?.red)) ? `rgb(${color.red}, ${color.green}, ${color.blue})`: (_.isNumber((color as IHSB)?.hue)) ? `hsv(${Math.round(color.hue)}, ${Math.round(color.saturation)}, ${Math.round(color.brightness)})` : _.isEmpty(color) ? "Click to assign" : color
  return <ColorPickerStoreContext.Provider value={store}>
    <Popover2
      interactionKind="click"
      minimal
      content={<SketchPicker
        color={store.activeColor.rgb}
        onChange={color => store.onColorChange(color, colorField)}
      />}
    >
        <div className={css.swatches}>
            <span className={css.backgroundColor} style={{backgroundColor: hex ?? 'var(--default-swatch-color)'}}>{`  ${colorText}`}</span>
        </div>
    </Popover2>
  </ColorPickerStoreContext.Provider>
})




// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/FieldRenderers/ColorPicker.tsx